import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    ArrowUpRight,
    Mail,
    Instagram,
    Linkedin,
    Globe,
    Sparkles,
} from 'lucide-react';
import ResumeCard from '@/components/portfolio/ResumeCard';
import CareerTimeline from '@/components/portfolio/CareerTimeline';
import Credentials from '@/components/portfolio/Credentials';
import PortraitCard from '@/components/portfolio/PortraitCard';
import { InstagramFeed } from '@/components/portfolio/InstagramFeed';
import { Seo, SITE_URL } from '@/lib/seo';

const PORTRAIT = '/avatars/about.webp';
const INSTAGRAM_URL = 'https://www.instagram.com/anshuhim/';
const INSTAGRAM_HANDLE = '@anshuhim';

const focusAreas = [
    {
        head: 'Research & discovery',
        items: ['User research', 'Workflow analysis', 'System modernization'],
    },
    {
        head: 'Enterprise craft',
        items: [
            'UX for enterprise applications',
            'Scalable design systems',
            'Responsive web applications',
        ],
    },
    {
        head: 'Delivery',
        items: ['Figma-driven design delivery', 'Developer handoff', 'Agile collaboration'],
    },
];

const collaborators = [
    'Product Owners',
    'Business Analysts',
    'Architects',
    'Engineering teams',
];

const elsewhere = [
    {
        label: 'LinkedIn',
        handle: '/in/anshuu',
        href: 'https://www.linkedin.com/in/anshuu/',
        icon: Linkedin,
    },
    {
        label: 'Email',
        handle: 'himanshu.tanty08@gmail.com',
        href: 'mailto:himanshu.tanty08@gmail.com',
        icon: Mail,
    },
    {
        label: 'Instagram',
        handle: '@anshuhim',
        href: 'https://www.instagram.com/anshuhim/',
        icon: Instagram,
    },
    {
        label: 'Behance',
        handle: '/himanshuu',
        href: 'https://www.behance.net/himanshuu',
        icon: Globe,
    },
];

const Label = ({ label }) => (
    <div className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
        <span className="w-6 h-[1px] bg-[var(--line-2)]" />
        <span>{label}</span>
    </div>
);

const About = () => {
    return (
        <>
            <Seo
                title="About Himanshu Tanty · Product Designer"
                description="How I work, my career from 2012 to today, and the design thinking behind my enterprise, AI and SaaS product work."
                canonical={`${SITE_URL}/about`}
            />
        <section
            id="about"
            data-testid="about-page"
            className="relative px-4 md:px-6 pt-24 md:pt-28 pb-12 md:pb-16"
        >
            <div className="mx-auto max-w-[1400px] flex flex-col gap-6 md:gap-8">
                {/* ───── HERO ROW ───── */}
                <div className="grid grid-cols-12 gap-3 md:gap-4">
                    {/* Left: heading + paragraph */}
                    <div className="col-span-12 md:col-span-8 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-10 flex flex-col justify-between gap-6 md:gap-8 min-h-[520px]">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <Label label="About" />
                            <span
                                data-testid="about-location-chip"
                                className="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] font-mono-brand text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[var(--ink-2)]"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot shrink-0" />
                                <span className="whitespace-nowrap">Bengaluru, India</span>
                            </span>
                        </div>

                        <div>
                            <motion.h1
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
                                data-testid="about-heading"
                                className="font-display leading-[0.92] text-[12vw] md:text-[7vw] text-[var(--ink)]"
                            >
                                <span className="font-display-italic text-[var(--accent)]">
                                    About
                                </span>
                                <span className="text-[var(--ink-2)]">.</span>
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.25, duration: 0.9 }}
                                data-testid="about-paragraph"
                                className="mt-8 md:mt-10 font-sans-brand text-[15px] md:text-[17px] leading-[1.65] text-[var(--ink-2)]"
                            >
                                <span className="text-[var(--ink)] font-medium">
                                    Senior product designer
                                </span>{' '}
                                with{' '}
                                <span className="text-[var(--ink)]">
                                    over a decade of experience
                                </span>{' '}
                                building{' '}
                                <span className="text-[var(--ink)]">
                                    enterprise SaaS, cloud platforms
                                </span>{' '}
                                and data-heavy products. I take tangled
                                workflows and make them feel obvious, working
                                shoulder to shoulder with{' '}
                                <span className="font-display-italic text-[var(--accent)]">
                                    product and engineering
                                </span>{' '}
                                to ship, not just design.
                            </motion.p>
                        </div>

                        {/* CTAs */}
                        <div className="mt-10 md:mt-12 flex items-center gap-3 flex-wrap">
                            <Link
                                to="/#contact"
                                data-cursor="hover"
                                className="inline-flex items-center gap-2 pl-5 pr-2 py-2 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                            >
                                Get in touch
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                                    <ArrowUpRight size={14} />
                                </span>
                            </Link>
                            <Link
                                to="/#work"
                                data-cursor="hover"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[var(--line-2)] text-sm font-medium text-[var(--ink)] hover:bg-white/5 transition-colors"
                            >
                                See my work
                                <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </div>

                    {/* Right: portrait */}
                    <PortraitCard
                        src={PORTRAIT}
                        name="Himanshu Kumar Tanty"
                        tagline="Designer who loves a tidy, usable interface."
                    />
                </div>

                {/* ───── FOCUS ROW ───── */}
                <div className="grid grid-cols-12 gap-3 md:gap-4">
                    {focusAreas.map((f, i) => (
                        <motion.div
                            key={f.head}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-8%' }}
                            transition={{ duration: 0.7, delay: i * 0.08 }}
                            data-testid={`about-focus-${i}`}
                            className="col-span-12 md:col-span-4 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-7 flex flex-col gap-5"
                        >
                            <div className="flex items-center justify-between">
                                <Label n={String(i + 3).padStart(2, '0')} label={f.head} />
                                <Sparkles size={12} className="text-[var(--accent)]" />
                            </div>
                            <ul className="flex flex-col gap-2.5 mt-2">
                                {f.items.map((it) => (
                                    <li
                                        key={it}
                                        className="group flex items-baseline gap-3 text-[var(--ink)]"
                                    >
                                        <span className="font-mono-brand text-[10px] tabular-nums text-[var(--ink-3)] group-hover:text-[var(--accent)] transition-colors">
                                            /
                                        </span>
                                        <span className="font-display-italic text-xl md:text-[22px] leading-tight">
                                            {it}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>

                {/* ───── COLLABORATORS ROW ───── */}
                <div className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-10">
                    <div className="flex items-center justify-between gap-4 flex-wrap mb-6 md:mb-8">
                        <Label n="06" label="I work with" />
                        <span className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            Cross-functional · Agile
                        </span>
                    </div>
                    <div className="flex flex-wrap lg:flex-nowrap lg:justify-between items-baseline gap-x-6 gap-y-3">
                        {collaborators.map((c, i) => (
                            <motion.div
                                key={c}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.06, duration: 0.55 }}
                                className="flex items-baseline gap-3"
                            >
                                <span className="font-display text-2xl md:text-3xl lg:text-[clamp(1.4rem,2.6vw,2.4rem)] whitespace-nowrap text-[var(--ink)] leading-none">
                                    {c}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* ───── INSTAGRAM ROW ───── */}
                <div className="grid grid-cols-12 gap-3 md:gap-4">
                    {/* Left — follow card */}
                    <div className="col-span-12 md:col-span-4 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8 flex flex-col justify-between min-h-[190px]">
                        <div className="flex items-center justify-between">
                            <Label n="07" label="On Instagram" />
                            <Instagram size={16} className="text-[var(--accent)]" />
                        </div>
                        <div>
                            <div className="font-display leading-[0.95] text-2xl md:text-3xl text-[var(--ink)]">
                                Life beyond{' '}
                                <span className="font-display-italic text-[var(--accent)]">
                                    work
                                </span>
                                <span className="text-[var(--ink-2)]">.</span>
                            </div>
                            <p className="mt-3 font-sans-brand text-[12px] md:text-[13px] text-[var(--ink-2)] max-w-[36ch]">
                                Reels, stories and moments from my everyday.
                            </p>
                            <span className="mt-3 inline-block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                                {INSTAGRAM_HANDLE}
                            </span>
                        </div>
                        <div className="mt-5 flex flex-col items-start gap-3">
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="about-instagram-follow"
                                data-cursor="hover"
                                className="inline-flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-[13px] font-semibold hover:opacity-90 transition-opacity"
                            >
                                <Instagram size={14} />
                                Follow
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                                    <ArrowUpRight size={13} />
                                </span>
                            </a>
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="instagram-view-all"
                                data-cursor="hover"
                                className="group inline-flex items-center gap-1.5 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)] hover:text-[var(--accent)] transition-colors"
                            >
                                View all on Instagram
                                <ArrowUpRight
                                    size={12}
                                    className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300"
                                />
                            </a>
                        </div>
                    </div>

                    {/* Right — live feed (Behold) or themed placeholder mosaic */}
                    <div className="col-span-12 md:col-span-8">
                        <InstagramFeed
                            profileUrl={INSTAGRAM_URL}
                            handle={INSTAGRAM_HANDLE}
                        />
                    </div>
                </div>

                {/* ───── ELSEWHERE ROW ───── */}
                <div className="grid grid-cols-12 gap-3 md:gap-4">
                    <div className="col-span-12 md:col-span-4 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8 flex flex-col justify-between min-h-[280px]">
                        <Label n="08" label="Elsewhere" />
                        <div>
                            <div className="font-display leading-[0.95] text-5xl md:text-6xl text-[var(--ink)]">
                                Find me
                                <br />
                                <span className="font-display-italic text-[var(--accent)]">
                                    online
                                </span>
                                <span className="text-[var(--ink-2)]">.</span>
                            </div>
                            <p className="mt-5 font-sans-brand text-[13px] md:text-sm text-[var(--ink-2)] max-w-[36ch]">
                                Most days I&apos;m on LinkedIn, so drop a note,
                                say hi, or share the problem you&apos;re trying
                                to solve.
                            </p>
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                        {elsewhere.map((s) => {
                            const Icon = s.icon;
                            return (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`about-link-${s.label.toLowerCase()}`}
                                    data-cursor="hover"
                                    className="group relative flex items-center justify-between gap-4 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6 hover:border-[var(--accent)] transition-all duration-500"
                                >
                                    <div className="flex items-center gap-4">
                                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--line-2)] text-[var(--ink)] group-hover:text-[var(--accent)] group-hover:border-[var(--accent)] transition-colors">
                                            <Icon size={16} />
                                        </span>
                                        <div>
                                            <div className="font-display-italic text-xl md:text-2xl text-[var(--ink)] group-hover:text-[var(--accent)] transition-colors">
                                                {s.label}
                                            </div>
                                            <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)] mt-1">
                                                {s.handle}
                                            </div>
                                        </div>
                                    </div>
                                    <ArrowUpRight
                                        size={16}
                                        className="text-[var(--ink-3)] group-hover:text-[var(--accent)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300"
                                    />
                                </a>
                            );
                        })}
                    </div>
                </div>

            </div>
        </section>
        <CareerTimeline />
        <Credentials />
        <section className="px-4 md:px-6 pb-12 md:pb-16">
            <div className="mx-auto max-w-[1400px]">
                <ResumeCard number="09" />
            </div>
        </section>
        </>
    );
};

export default About;
